import { Box, Chip, styled, Typography } from '@mui/material'
import numeral from 'numeral'
import { useMemo } from 'react'

import { PreSaleLotteryApplicantDetailFragment } from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

type LotteryChoiceItemProps = {
  index: number
  choiceLength?: number
  choiceItemDetail: PreSaleLotteryApplicantDetailFragment['choiceItemDetails'][number]
  winChoiceNumber: PreSaleLotteryApplicantDetailFragment['winChoiceNumber']
  /** 応募単位での当落ステータス */
  applicantStatus: PreSaleLotteryApplicantDetailFragment['status']
  hidePrice?: boolean
  eventTitle?: string
}

export const LotteryChoiceItem: Organism<LotteryChoiceItemProps> = ({
  index,
  choiceLength = 1,
  choiceItemDetail,
  winChoiceNumber,
  applicantStatus,
  hidePrice,
  eventTitle,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')

  const status = useMemo(() => {
    if (applicantStatus === '30_waiting_lottery') {
      return {
        label: t('30_waiting_lottery'),
        bgcolor: 'secondary.main',
        color: 'white',
      }
    } else if (
      (applicantStatus === '10_win' || applicantStatus === '11_win_paid') &&
      winChoiceNumber === index + 1
    ) {
      return {
        label: t(applicantStatus),
        bgcolor: 'primary.main',
        color: 'white',
      }
    } else if (
      applicantStatus === '20_waiting_payment' &&
      winChoiceNumber === index + 1
    ) {
      return {
        label: t('20_waiting_payment'),
        bgcolor: 'error.main',
        color: 'white',
      }
    } else if (applicantStatus === '10_win' && winChoiceNumber === index + 1) {
      return {
        label: t('20_waiting_payment'),
        bgcolor: 'error.main',
        color: 'white',
      }
    } else if (applicantStatus === '50_invalid') {
      return {
        label: t('50_invalid'),
        bgcolor: 'grey.100',
        color: 'text.disabled',
      }
    } else if (applicantStatus === '60_canceled') {
      return {
        label: t('60_canceled'),
        bgcolor: 'grey.100',
        color: 'text.disabled',
      }
    } else {
      return {
        label: t('40_lose'),
        bgcolor: 'grey.300',
        color: 'text.secondary',
      }
    }
  }, [applicantStatus, winChoiceNumber, index, t])

  if (!choiceItemDetail?.item) return null

  return (
    <OrganismFrame>
      {choiceLength > 1 && (
        <Chip
          variant="outlined"
          sx={{
            mt: 2,
            mb: 1,
            typography: 'subtitle2',
            height: '20px',
            lineHeight: 1.8,
            bgcolor: 'background.default',
            display: 'block',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
          size="small"
          label={t('choicePriority', { number: ++index })}
        />
      )}
      <Chip
        size="small"
        sx={{
          height: 20,
          borderRadius: '10px',
          bgcolor: status.bgcolor,
          py: 1,
          px: 4,
          '.MuiChip-label': {
            px: 0,
          },
        }}
        label={
          <Typography color={status.color} variant="subtitle2">
            {status.label}
          </Typography>
        }
      />
      <Typography sx={{ mt: 4 }} variant="subtitle1" color="text.secondary">
        {t('ticketTitle')}
      </Typography>
      <Typography sx={{ mt: 1 }} variant="body2">
        {choiceItemDetail?.item.title}
      </Typography>
      <Typography sx={{ mt: 1 }} variant="body2">
        {choiceItemDetail?.skuUnit?.unitName}
      </Typography>
      {!!eventTitle && (
        <>
          <Border sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="text.secondary">
            {t('eventTitle')}
          </Typography>
          <Typography sx={{ mt: 1 }} variant="body2">
            {eventTitle}
          </Typography>
        </>
      )}
      {!hidePrice && (
        <>
          <Border sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="text.secondary">
            {t('price')}
          </Typography>
          <Typography sx={{ mt: 1 }} variant="body2">
            {t('priceValue', {
              price: numeral(
                choiceItemDetail?.skuUnit?.price ?? choiceItemDetail?.item.price
              ).format('0,0'),
            })}
          </Typography>
        </>
      )}
      <Border sx={{ my: 2 }} />
      <Typography variant="subtitle1" color="text.secondary">
        {t('applicationNumber')}
      </Typography>
      <Typography sx={{ mt: 1 }} variant="body2">
        {t('applicationNumberValue', { count: choiceItemDetail?.count })}
      </Typography>
    </OrganismFrame>
  )
}

const Border = styled('div')(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.palette.grey['300'],
})) as typeof Box
